import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { COLORS, FONTS } from "@/lib/constants";
import { INK, MUTED_36, MUTED_60, FONT as YFONT, ROOMS, capsuleStyle } from "@/lib/yuoDesign";
import InputRow from "@/components/yuo/InputRow";
import ThemeToggle from "@/components/ThemeToggle";
import { MobileHeader, MobileHeaderButton } from "@/components/mobile/MobileHeader";
import MoreSheet from "@/components/mobile/MoreSheet";
import { useResponsive } from "@/lib/responsive";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  COLOR_SLOTS,
  blocksToDraftText,
  blocksToHtml,
  characterMapToList,
  charactersToMap,
  colorForKey,
  draftTextToBlocks,
  monoFontFamily,
  nextOrderForColor,
  slotMetaForKey,
} from "@/lib/screenwriter";

const hexToRgb = (hex) => {
  const h = (hex || "#000000").replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `${r},${g},${b}`;
};

/**
 * Screenwriter — 1:1 web port of the original mobile screen.
 * Preserves: title, color palette (5 slots), characters chips (tap insert),
 * active-draft toggle (Raw / Main), Raw Draft textarea, Send to Main Draft,
 * Import from…, Export PDF, Courier · A4 · editable Main Draft, character
 * add/edit/delete modal, scripts list modal.
 * Adds:
 *   1) Translate button next to the "Main Draft" tab.
 *   2) Export PDF -> Original / Colored choice.
 */
export default function Screenwriter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const plan = user?.plan || "free";
  const isMaster = plan === "master";
  const isProOrHigher = plan === "pro" || plan === "master";
  const { readingMaxWidth, gutter, isDesktop, isPhone } = useResponsive();
  const [moreOpen, setMoreOpen] = useState(false);
  const [editChars, setEditChars] = useState(false);

  const [scriptId, setScriptId] = useState(null);
  const [title, setTitle] = useState("Untitled Script");
  const [rawText, setRawText] = useState("");
  const [mainDraft, setMainDraft] = useState("");
  const [characters, setCharacters] = useState([]);
  const [activeDraft, setActiveDraft] = useState("raw");
  const [formatting, setFormatting] = useState(false);
  const [saveHint, setSaveHint] = useState(null);
  const [askDraft, setAskDraft] = useState("");
  const [savingScript, setSavingScript] = useState(false);
  const [error, setError] = useState(null);

  const [charModal, setCharModal] = useState(null);   // { colorKey, edit? }
  const [charDraft, setCharDraft] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [scriptsOpen, setScriptsOpen] = useState(false);
  const [scriptsList, setScriptsList] = useState([]);
  const [scriptsCount, setScriptsCount] = useState(0);
  const [importOpen, setImportOpen] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);

  const rawRef = useRef(null);
  const mainRef = useRef(null);
  const rawSelRef = useRef({ start: 0, end: 0 });
  const mainSelRef = useRef({ start: 0, end: 0 });

  const hydrate = (doc) => {
    setScriptId(doc.script_id);
    setTitle(doc.title || "Untitled Script");
    setRawText(doc.raw_text || "");
    setMainDraft(doc.main_draft || blocksToDraftText(doc.blocks || []));
    if (Array.isArray(doc.characters) && doc.characters.length) setCharacters(doc.characters);
    else setCharacters(characterMapToList(doc.character_map || {}));
    setError(null);
  };

  // Load scripts count once on mount so the History pill shows the real number.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.screenwriterListScripts();
        const list = r?.scripts || r || [];
        setScriptsCount(list.length);
      } catch { /* silent */ }
    })();
  }, []);

  // Autosave (debounced) once we have a script id.
  useEffect(() => {
    if (!scriptId) return;
    const t = setTimeout(async () => {
      try {
        await api.screenwriterUpdateScript(scriptId, {
          title,
          character_map: charactersToMap(characters),
          characters,
          raw_text: rawText,
          main_draft: mainDraft,
        });
        setSaveHint("Saved");
        setTimeout(() => setSaveHint(null), 1200);
      } catch { /* silent */ }
    }, 900);
    return () => clearTimeout(t);
  }, [rawText, mainDraft, title, characters, scriptId]);

  const ensureScript = async () => {
    if (scriptId) return scriptId;
    const doc = await api.screenwriterCreateScript({
      title,
      character_map: charactersToMap(characters),
      characters,
      raw_text: rawText,
      main_draft: mainDraft,
    });
    setScriptId(doc.script_id);
    return doc.script_id;
  };

  // ---- Characters ---------------------------------------------------------
  const openCharModal = (colorKey, edit) => { setCharDraft(edit?.name || ""); setCharModal({ colorKey, edit }); };
  const saveCharacter = () => {
    if (!charModal) return;
    const name = charDraft.trim();
    if (!name) return;
    if (charModal.edit) {
      const oldName = charModal.edit.name;
      const newName = name.toUpperCase();
      setCharacters((prev) => prev.map((c) => (c.id === charModal.edit.id ? { ...c, name: newName } : c)));
      if (oldName && newName && oldName !== newName) {
        const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        let pattern;
        try { pattern = new RegExp(`(?<![\\p{L}\\p{N}])${escapeRe(oldName)}(?![\\p{L}\\p{N}])`, "giu"); }
        catch { pattern = new RegExp(escapeRe(oldName), "gi"); }
        setRawText((prev) => prev.replace(pattern, newName));
        setMainDraft((prev) => prev.replace(pattern, newName));
      }
    } else {
      const nextOrder = nextOrderForColor(characters, charModal.colorKey);
      const id = `${charModal.colorKey}-${nextOrder}-${Date.now().toString(36).slice(-4)}`;
      setCharacters((prev) => [...prev, { id, colorKey: charModal.colorKey, order: nextOrder, name: name.toUpperCase() }]);
    }
    setCharModal(null);
    setCharDraft("");
    ensureScript();
  };
  const deleteCharacter = (id) => setCharacters((prev) => prev.filter((c) => c.id !== id));

  // ---- Insert into active draft ------------------------------------------
  const insertIntoActive = (snippet, opts = {}) => {
    const setter = activeDraft === "raw" ? setRawText : setMainDraft;
    const ref = activeDraft === "raw" ? rawSelRef : mainSelRef;
    const sel = ref.current;
    setter((prev) => {
      const before = prev.slice(0, sel.start);
      const after = prev.slice(sel.end);
      const prefix = opts.newline && before && !before.endsWith("\n\n") ? (before.endsWith("\n") ? "\n" : "\n\n") : "";
      return `${before}${prefix}${snippet}${after}`;
    });
  };

  const insertCharacter = (c) => {
    const slot = slotMetaForKey(c.colorKey);
    const snippet = slot.isLocation ? `${c.name}` : `${c.name}: `;
    insertIntoActive(snippet, { newline: true });
    ensureScript();
  };

  // ---- Format (Send to Main Draft) ---------------------------------------
  const runFormat = async () => {
    if (!isProOrHigher) { navigate("/paywall?reason=screenwriter"); return; }
    if (!rawText.trim()) { setError("Write at least one line in the Raw Draft."); return; }
    setFormatting(true); setError(null);
    try {
      const r = await api.screenwriterFormat({ text: rawText, character_map: charactersToMap(characters) });
      const draft = blocksToDraftText(r.blocks || []);
      setMainDraft((prev) => (prev ? `${prev}\n\n${draft}` : draft));
      setActiveDraft("main");
      await ensureScript();
    } catch (e) {
      if (e?.status === 402) navigate("/paywall?reason=screenwriter");
      else setError(e?.message || "Formatting failed");
    } finally { setFormatting(false); }
  };

  // ---- Export ------------------------------------------------------------
  const doExport = async (colored) => {
    setExportOpen(false);
    setExporting(true);
    try {
      const blocks = draftTextToBlocks(mainDraft || rawText, characters);
      const html = blocksToHtml(blocks, characters, { title: title || "Untitled Script", colored });
      const w = window.open("", "_blank");
      if (w) {
        w.document.open();
        w.document.write(html);
        w.document.close();
        setTimeout(() => { try { w.focus(); w.print(); } catch { /* noop */ } }, 400);
      }
    } catch (e) {
      alert(e?.message || "Could not export");
    } finally { setExporting(false); }
  };

  // ---- Save / Load scripts ----------------------------------------------
  const saveCurrent = async () => {
    setSavingScript(true);
    try {
      const id = await ensureScript();
      await api.screenwriterUpdateScript(id, {
        title, character_map: charactersToMap(characters), characters, raw_text: rawText, main_draft: mainDraft,
      });
      setSaveHint("Saved");
      setTimeout(() => setSaveHint(null), 1400);
      // refresh count silently
      try {
        const r = await api.screenwriterListScripts();
        const list = r?.scripts || r || [];
        setScriptsCount(list.length);
      } catch { /* silent */ }
    } catch (e) { setError(e?.message || "Could not save"); }
    finally { setSavingScript(false); }
  };
  const openScriptsList = async () => {
    setScriptsOpen(true);
    try {
      const r = await api.screenwriterListScripts();
      const list = r?.scripts || r || [];
      setScriptsList(list);
      setScriptsCount(list.length);
    } catch { /* silent */ }
  };
  const openScript = (doc) => { hydrate(doc); setScriptsOpen(false); };
  const newScript = () => { setScriptId(null); setTitle("Untitled Script"); setRawText(""); setMainDraft(""); setCharacters([]); setScriptsOpen(false); };
  const removeScript = async (id) => {
    try { await api.screenwriterDeleteScript(id); setScriptsList((prev) => prev.filter((s) => s.script_id !== id)); if (id === scriptId) newScript(); } catch { /* silent */ }
  };

  // ---- Translate --------------------------------------------------------
  const applyTranslated = (text, replaceAll) => {
    if (replaceAll) setMainDraft(text);
    else setMainDraft((prev) => (prev ? `${prev}\n\n${text}` : text));
    setActiveDraft("main");
    ensureScript();
  };

  // ---- Render ------------------------------------------------------------
  return (
    <div
      data-testid="screenwriter-screen"
      style={{ position: "fixed", inset: 0, background: "var(--yuo-page-creator)", display: "flex", flexDirection: "column" }}
    >
      {/* Header — matches Content: coral orb + CONTENT · expression + HISTORY / NEW */}
      {isPhone && (
        <MobileHeader room="content" title="Content" sub="expression" testId="screenwriter-m-header"
          actions={<>
            {saveHint && <span data-testid="screenwriter-save-hint" style={{ color: MUTED_36, fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", whiteSpace: "nowrap" }}>{saveHint}</span>}
            <ThemeToggle compact />
            <MobileHeaderButton room="content" solid onClick={saveCurrent} disabled={savingScript} testId="screenwriter-save">{savingScript ? "…" : "Save"}</MobileHeaderButton>
          </>}
          onMore={() => setMoreOpen(true)} />
      )}
      {isPhone && (
        <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Content · more" testId="screenwriter-m-sheet" rows={[
          { label: `History · ${scriptsCount}`, hint: "my scripts", color: "content", onClick: openScriptsList, testId: "m-sw-history" },
          { label: "New script", hint: "start from a blank page", color: "content", right: "+", onClick: newScript, testId: "m-sw-new" },
          { label: "Import from…", hint: "diary · mirror · symbiosis", color: "mirror", onClick: () => setImportOpen(true), testId: "m-sw-import" },
          { label: "Export PDF", hint: "original or coloured", color: "symbiosis", onClick: () => setExportOpen(true), testId: "m-sw-export" },
          { label: "Back to Creator", hint: "chat", color: "content", onClick: () => navigate("/content"), testId: "m-sw-back" },
        ]} />
      )}
      {!isPhone && (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `14px ${gutter}px`, background: "transparent", flexShrink: 0, gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <span style={{
            width: 12, height: 12, borderRadius: "50%",
            background: "radial-gradient(38% 34% at 34% 26%, #fff, #FF7A66 20%, #7A2418 60%, #05060c 100%)",
            boxShadow: "0 0 10px rgba(var(--role-coral-rgb),0.6)",
          }} />
          <div style={{ color: INK, fontSize: 13, letterSpacing: "0.3em", textTransform: "uppercase" }}>
            Content
          </div>
          <div style={{ color: MUTED_36, fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase" }}>
            · expression
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {saveHint && <span style={{ color: COLORS.textMuted, fontSize: 10, fontFamily: FONTS.body, letterSpacing: "0.16em", textTransform: "uppercase" }}>{saveHint}</span>}
          <ThemeToggle />
          <button onClick={openScriptsList} data-testid="screenwriter-my-scripts"
            style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              background: "var(--yuo-surface-5)", color: INK,
              border: "none", borderRadius: 999, padding: "8px 18px",
              fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase",
              fontFamily: FONTS.body, cursor: "pointer",
            }}>
            <span style={{
              width: 10, height: 10, borderRadius: "50%",
              background: "radial-gradient(38% 34% at 34% 26%, #fff, #FF7A66 22%, #05060c 100%)",
              boxShadow: "0 0 8px rgba(var(--role-coral-rgb),0.55)",
            }} />
            History · {scriptsCount}
          </button>
          <button onClick={saveCurrent} disabled={savingScript} data-testid="screenwriter-save"
            style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              background: "var(--role-coral-pill-bg-08)",
              boxShadow: "var(--role-coral-pill-glow-22)",
              color: INK, border: "none", borderRadius: 999, padding: "8px 18px",
              fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", fontFamily: FONTS.body,
              cursor: "pointer", opacity: savingScript ? 0.6 : 1,
            }}>
            <span style={{
              width: 10, height: 10, borderRadius: "50%",
              background: "radial-gradient(38% 34% at 34% 26%, #fff, #FF7A66 22%, #05060c 100%)",
              boxShadow: "0 0 10px rgba(var(--role-coral-rgb),0.75)",
            }} />
            {savingScript ? "…" : "Save"}
          </button>
        </div>
      </div>
      )}

      {/* Scrollable body */}
      <div className={isPhone ? "m-scroll" : undefined} style={{ flex: 1, overflowY: "auto" }}>
        {isPhone && (
          <PhoneBody
            title={title} setTitle={(v) => { setTitle(v); if (!scriptId) ensureScript(); }}
            characters={characters} editing={editChars} onToggleEdit={() => setEditChars((v) => !v)}
            onInsert={insertCharacter} onRename={(c) => openCharModal(c.colorKey, c)} onDelete={deleteCharacter} onAdd={(key) => openCharModal(key)}
            slotMetaForKey={slotMetaForKey}
            activeDraft={activeDraft} setActiveDraft={setActiveDraft} onTranslate={() => setTranslateOpen(true)}
            rawText={rawText} setRawText={setRawText} mainDraft={mainDraft} setMainDraft={setMainDraft}
            rawRef={rawRef} mainRef={mainRef} rawSelRef={rawSelRef} mainSelRef={mainSelRef}
            error={error} formatting={formatting} onFormat={runFormat} exporting={exporting} onExport={() => setExportOpen(true)}
          />
        )}
        {!isPhone && (
        <div style={{ padding: `18px ${gutter}px 60px`, maxWidth: readingMaxWidth, margin: "0 auto", width: "100%" }}>
          <input
            value={title}
            onChange={(e) => { setTitle(e.target.value); if (!scriptId) ensureScript(); }}
            placeholder="Untitled Script"
            data-testid="screenwriter-title"
            style={{
              width: "100%",
              background: "var(--yuo-surface-5)",
              border: "none", outline: "none",
              borderRadius: 999,
              padding: "16px 24px",
              color: INK, fontFamily: FONTS.body,
              fontSize: 18, letterSpacing: -0.2,
              marginBottom: 22,
              boxShadow: "var(--role-coral-title-glow)",
            }}
          />

          {/* Color palette */}
          <Section>Color palette · tap a color to create</Section>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 22 }}>
            {COLOR_SLOTS.map((s) => {
              const rgb = hexToRgb(s.color);
              return (
                <button
                  key={s.key}
                  onClick={() => openCharModal(s.key)}
                  data-testid={`palette-${s.key}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    background: `radial-gradient(120% 160% at 30% 0%, rgba(${rgb},0.24), rgba(${rgb},0.06))`,
                    boxShadow: `0 0 22px rgba(${rgb},0.28)`,
                    border: "none", borderRadius: 999,
                    padding: "9px 18px", cursor: "pointer",
                    color: COLORS.textPrimary, fontFamily: FONTS.body,
                  }}
                >
                  <span style={{
                    width: 14, height: 14, borderRadius: "50%",
                    background: `radial-gradient(38% 34% at 34% 26%, #fff, ${s.color} 30%, rgba(${rgb},0.18) 62%, #05060c 100%)`,
                    boxShadow: `0 0 12px rgba(${rgb},0.7)`,
                  }} />
                  <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.2 }}>{s.label}</span>
                  <span style={{ color: COLORS.textMuted, fontSize: 11 }}>{s.hint}</span>
                </button>
              );
            })}
          </div>

          {/* Characters */}
          {characters.length > 0 && (
            <>
              <Section>Characters · tap a chip to insert the name</Section>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
                {characters.map((c) => {
                  const color = colorForKey(c.colorKey) || COLORS.textMuted;
                  return (
                    <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <button
                        onClick={() => insertCharacter(c)}
                        onDoubleClick={() => openCharModal(c.colorKey, c)}
                        data-testid={`chip-${c.id}`}
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          background: `${color}18`, border: `1px solid ${color}`, borderRadius: 999,
                          padding: "6px 12px",
                        }}
                      >
                        <span style={{ width: 20, height: 20, borderRadius: 10, background: color, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--yuo-on-accent)", fontSize: 10, fontWeight: 700 }}>{c.order}</span>
                        <span style={{ color: COLORS.textPrimary, fontFamily: FONTS.body, fontSize: 12, fontWeight: 700 }}>{c.name}</span>
                      </button>
                      <button onClick={() => deleteCharacter(c.id)} data-testid={`chip-del-${c.id}`} style={{ padding: "0 6px", color: COLORS.textMuted, fontSize: 16 }}>×</button>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Active draft toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase" }}>Active draft:</span>
            <button onClick={() => setActiveDraft("raw")} data-testid="active-raw"
              style={{
                border: "none", borderRadius: 999, padding: "8px 18px",
                fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase",
                color: COLORS.textPrimary, fontFamily: FONTS.body, cursor: "pointer",
                background: activeDraft === "raw"
                  ? "var(--role-blue-pill-bg-08)"
                  : "var(--yuo-surface-5)",
                boxShadow: activeDraft === "raw" ? "var(--role-blue-pill-glow-26)" : "none",
              }}>
              Raw Draft
            </button>
            <button onClick={() => setActiveDraft("main")} data-testid="active-main"
              style={{
                border: "none", borderRadius: 999, padding: "8px 18px",
                fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase",
                color: COLORS.textPrimary, fontFamily: FONTS.body, cursor: "pointer",
                background: activeDraft === "main"
                  ? "var(--role-blue-pill-bg-08)"
                  : "var(--yuo-surface-5)",
                boxShadow: activeDraft === "main" ? "var(--role-blue-pill-glow-26)" : "none",
              }}>
              Main Draft
            </button>
            <button onClick={() => setTranslateOpen(true)} data-testid="screenwriter-translate"
              style={{
                marginLeft: 4, border: "none", borderRadius: 999, padding: "8px 18px",
                fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase",
                color: COLORS.textPrimary, fontFamily: FONTS.body, cursor: "pointer",
                background: "var(--role-green-pill-bg-06)",
                boxShadow: "var(--role-green-pill-glow-22)",
              }}>
              Translate
            </button>
          </div>

          {/* Raw Draft — cohesive glowing capsule (like Symbiosis Field) */}
          <div style={{
            padding: "22px 24px 20px",
            borderRadius: 28,
            background:
              "var(--role-blue-panel-bg)",
            boxShadow:
              "var(--role-blue-panel-glow)",
            marginBottom: 22,
          }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                <DraftHeader label="Raw Draft" hint="free-form scratch pad" colored={false} />
              </div>
              <div data-testid="raw-char-count" style={{ color: MUTED_36, fontFamily: FONTS.body, fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                {rawText.length} char · {rawText.trim() ? rawText.trim().split(/\s+/).length : 0} words
              </div>
            </div>
            <textarea
              ref={rawRef}
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              onFocus={() => setActiveDraft("raw")}
              onSelect={(e) => (rawSelRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd })}
              data-testid="raw-draft"
              style={{
                width: "100%", boxSizing: "border-box",
                background: "var(--yuo-paper)", border: "none", borderRadius: 20,
                padding: "18px 22px", color: COLORS.textPrimary, fontFamily: monoFontFamily, fontSize: 14,
                lineHeight: "22px", outline: "none", minHeight: 200,
                boxShadow: "var(--role-blue-field-glow)",
                display: "block",
              }}
            />
          </div>

          {error && <div style={{ color: COLORS.error, fontFamily: FONTS.body, fontSize: 13, marginBottom: 10 }}>{error}</div>}

          {/* Action buttons — exactly three, in order */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 26 }}>
            <button onClick={runFormat} disabled={formatting} data-testid="btn-send-main"
              style={{
                display: "inline-flex", alignItems: "center", gap: 12,
                background: "var(--role-blue-pill-bg-strong)",
                boxShadow: "var(--role-blue-pill-glow-34)",
                color: "var(--yuo-ink)", border: "none", borderRadius: 999,
                padding: "13px 24px", fontFamily: FONTS.body,
                fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                opacity: formatting ? 0.6 : 1, cursor: "pointer",
              }}>
              <span style={{
                width: 15, height: 15, borderRadius: "50%",
                background: "radial-gradient(38% 34% at 34% 26%, #fff, #4F9DFF 22%, #05060c 100%)",
                boxShadow: "0 0 12px rgba(var(--role-blue-rgb),0.8)",
              }} />
              {formatting ? "…" : "Send to Main Draft"}
            </button>
            <button onClick={() => setImportOpen(true)} data-testid="btn-import"
              style={{
                background: "var(--yuo-surface-5)", color: COLORS.textPrimary,
                border: "none", borderRadius: 999, padding: "13px 22px",
                fontFamily: FONTS.body, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                cursor: "pointer",
              }}>
              Import from…
            </button>
            <button onClick={() => setExportOpen(true)} disabled={exporting} data-testid="btn-export"
              style={{
                background: "var(--yuo-surface-5)", color: COLORS.textPrimary,
                border: "none", borderRadius: 999, padding: "13px 22px",
                fontFamily: FONTS.body, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                cursor: "pointer", opacity: exporting ? 0.6 : 1,
              }}>
              {exporting ? "…" : "Export PDF"}
            </button>
          </div>

          {/* Main Draft — cohesive glowing capsule (Courier · A4) */}
          <div style={{
            padding: "22px 24px 20px",
            borderRadius: 28,
            background:
              "var(--role-green-panel-bg)",
            boxShadow:
              "var(--role-green-panel-glow)",
          }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
              <DraftHeader label="Main Draft" hint="Courier · A4 · editable" colored={true} />
              <div data-testid="main-char-count" style={{ color: MUTED_36, fontFamily: FONTS.body, fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                {mainDraft.length} char · {mainDraft.trim() ? mainDraft.trim().split(/\s+/).length : 0} words
              </div>
            </div>
            <textarea
              ref={mainRef}
              value={mainDraft}
              onChange={(e) => setMainDraft(e.target.value)}
              onFocus={() => setActiveDraft("main")}
              onSelect={(e) => (mainSelRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd })}
              placeholder="After you tap 'Send to Main Draft' the formatted screenplay appears here. Edit freely, add (parentheticals), and describe action."
              data-testid="main-draft"
              style={{
                width: "100%", boxSizing: "border-box",
                background: "var(--yuo-paper)", border: "none", borderRadius: 20,
                padding: "18px 22px", color: COLORS.textPrimary, fontFamily: monoFontFamily, fontSize: 14,
                lineHeight: "22px", outline: "none", minHeight: 260,
                boxShadow: "var(--role-green-field-glow)",
                display: "block",
              }}
            />
          </div>
        </div>
        )}
      </div>

      {/* Bottom input row — coral ASK YUO + coral stone send, matches Content */}
      <div style={{ flex: "0 0 auto", position: "relative" }}>
        <InputRow
          room="content"
          value={askDraft || ""}
          onChange={setAskDraft}
          onSend={() => {
            const q = (askDraft || "").trim();
            if (!q) return;
            setAskDraft("");
            // Redirect to Content chat with the question queued
            try { sessionStorage.setItem("yuo_content_seed", q); } catch {}
            navigate("/content");
          }}
          onAskYuo={() => setAskDraft((p) => p.trim() ? `${p}\n\nYUO, ?` : "YUO, ?")}
          placeholder="speak…"
          inputTestId="screenwriter-input"
          sendTestId="screenwriter-send"
          askYuoTestId="screenwriter-ask-yuo"
        />
      </div>

      {/* Character add / edit modal */}
      {charModal && isPhone && (
        <PhoneCharSheet modal={charModal} draft={charDraft} setDraft={setCharDraft} onSave={saveCharacter}
          onCancel={() => setCharModal(null)} onDelete={() => { deleteCharacter(charModal.edit.id); setCharModal(null); }} />
      )}
      {charModal && !isPhone && (
        <div className="yuo-modal-backdrop" onClick={() => setCharModal(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="char-modal" style={{ margin: "auto", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: 20, width: "92%", maxWidth: isDesktop ? 460 : 480 }}>
            <div style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>
              {charModal.edit ? "Rename character" : "New character"}
            </div>
            <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 18, marginBottom: 12 }}>
              {slotMetaForKey(charModal.colorKey).label} · {slotMetaForKey(charModal.colorKey).hint}
            </div>
            <input
              autoFocus
              value={charDraft}
              onChange={(e) => setCharDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveCharacter()}
              placeholder={slotMetaForKey(charModal.colorKey).isLocation ? "INT. COFFEE SHOP — DAY" : "e.g. ALEX"}
              data-testid="char-input"
              style={{ width: "100%", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12, color: COLORS.textPrimary, fontFamily: FONTS.body, fontSize: 15, outline: "none", textTransform: "uppercase" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              {charModal.edit && (
                <button onClick={() => { deleteCharacter(charModal.edit.id); setCharModal(null); }} style={{ padding: "8px 12px", color: COLORS.error, fontFamily: FONTS.body, fontSize: 13 }}>
                  Delete
                </button>
              )}
              <button onClick={() => setCharModal(null)} style={{ padding: "8px 14px", color: COLORS.textSecondary, fontFamily: FONTS.body, fontSize: 13 }}>Cancel</button>
              <button onClick={saveCharacter} data-testid="char-save" style={{ background: COLORS.mirror, color: "var(--yuo-on-accent)", borderRadius: 999, padding: "8px 18px", fontFamily: FONTS.body, fontSize: 13, fontWeight: 700 }}>
                {charModal.edit ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export choice modal — Original vs Colored */}
      {exportOpen && (
        <div className="yuo-modal-backdrop" onClick={() => setExportOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="export-pdf-modal" style={{ margin: "auto", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: 24, width: "92%", maxWidth: 460 }}>
            <div style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>Save as PDF</div>
            <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 22, marginBottom: 16 }}>Choose your version</div>
            <button
              onClick={() => doExport(false)}
              data-testid="export-pdf-original"
              style={{ display: "block", width: "100%", textAlign: "left", padding: 14, borderRadius: 12, border: `1px solid var(--yuo-border)`, background: COLORS.bg, marginBottom: 10 }}
            >
              <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 17 }}>Original</div>
              <div style={{ color: COLORS.textSecondary, fontFamily: FONTS.body, fontSize: 12, marginTop: 4 }}>Standard professional screenplay — black &amp; white Courier.</div>
            </button>
            <button
              onClick={() => doExport(true)}
              data-testid="export-pdf-colored"
              style={{ display: "block", width: "100%", textAlign: "left", padding: 14, borderRadius: 12, border: `1px solid rgba(var(--role-blue-rgb),0.4)`, background: COLORS.bg }}
            >
              <div style={{ color: COLORS.mirror, fontFamily: FONTS.heading, fontSize: 17 }}>Colored</div>
              <div style={{ color: COLORS.textSecondary, fontFamily: FONTS.body, fontSize: 12, marginTop: 4 }}>Same professional format — each character&apos;s dialogue in their color.</div>
            </button>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              <button onClick={() => setExportOpen(false)} style={{ padding: "8px 14px", color: COLORS.textSecondary, fontFamily: FONTS.body, fontSize: 13 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Translate modal */}
      {translateOpen && (
        <TranslateModal
          scriptText={mainDraft || rawText}
          onClose={() => setTranslateOpen(false)}
          onApply={applyTranslated}
        />
      )}

      {/* Import modal — Live Import: Where Source Messages with character assignment */}
      {importOpen && (
        <ImportModal
          onClose={() => setImportOpen(false)}
          activeDraft={activeDraft}
          characters={characters}
          isMaster={isMaster}
          onIntegrate={({ text, destination }) => {
            if (destination === "main") setMainDraft((prev) => (prev ? `${prev}\n\n${text}` : text));
            else setRawText((prev) => (prev ? `${prev}\n\n${text}` : text));
            setImportOpen(false);
            ensureScript();
          }}
        />
      )}

      {/* Scripts list modal */}
      {scriptsOpen && (
        <div className="yuo-modal-backdrop" onClick={() => setScriptsOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 90,
            background: "var(--yuo-backdrop)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20,
          }}>
          <div onClick={(e) => e.stopPropagation()} data-testid="scripts-modal"
            style={{
              margin: "auto",
              background: "var(--role-coral-sheet-bg)",
              backdropFilter: "blur(18px)",
              WebkitBackdropFilter: "blur(18px)",
              boxShadow: "var(--role-coral-sheet-glow)",
              borderRadius: 30, padding: 26,
              width: "92%", maxWidth: 600, maxHeight: "85vh",
              display: "flex", flexDirection: "column",
              color: INK, fontFamily: FONTS.body,
            }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
              <div>
                <div style={{ color: "var(--role-coral-label)", fontFamily: FONTS.body, fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase" }}>My Scripts</div>
                <div style={{ color: INK, fontFamily: FONTS.heading, fontSize: 20, marginTop: 4 }}>Open or create</div>
              </div>
              <button onClick={() => setScriptsOpen(false)} data-testid="scripts-close"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: "transparent", border: "none", padding: 0, cursor: "pointer",
                  color: "var(--yuo-muted-55)", fontFamily: FONTS.body, fontSize: 10,
                  letterSpacing: "0.24em", textTransform: "uppercase",
                }}>
                <span style={{
                  width: 10, height: 10, borderRadius: "50%",
                  background: "radial-gradient(38% 34% at 34% 26%, #fff, #FF7A66 22%, #05060c 100%)",
                  boxShadow: "0 0 8px rgba(var(--role-coral-rgb),0.6)",
                }} />
                close
              </button>
            </div>
            <button onClick={newScript} data-testid="new-script"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 12,
                background: "var(--role-coral-pill-bg-strong)",
                boxShadow: "var(--role-coral-pill-glow-34)",
                border: "none", color: INK, borderRadius: 999,
                padding: "13px 22px", fontFamily: FONTS.body,
                fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase",
                marginBottom: 14, cursor: "pointer",
              }}>
              <span style={{
                width: 15, height: 15, borderRadius: "50%",
                background: "radial-gradient(38% 34% at 34% 26%, #fff, #FF7A66 22%, #05060c 100%)",
                boxShadow: "0 0 14px rgba(var(--role-coral-rgb),0.7)",
              }} />
              + New Script
            </button>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {(() => {
                // Hide anonymous "Untitled Script" placeholders from the list — only
                // rows the user has explicitly titled are worth surfacing.
                const named = (scriptsList || [])
                  .filter((s) => (s.title || "").trim() && s.title.trim() !== "Untitled Script")
                  .sort((a, b) => {
                    const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
                    const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
                    return tb - ta;
                  });
                if (named.length === 0) {
                  return (
                    <div style={{ padding: 24, color: MUTED_36, textAlign: "center",
                      fontSize: 10.5, letterSpacing: "0.24em", textTransform: "uppercase" }}>
                      No named scripts yet — tap + new script and give it a title.
                    </div>
                  );
                }
                return named.map((s) => (
                  <div key={s.script_id}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "14px 18px", borderRadius: 22, marginBottom: 10,
                      background: "var(--yuo-surface-5)",
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                    }}>
                    <button onClick={() => openScript(s)} data-testid={`open-script-${s.script_id}`}
                      style={{ flex: 1, textAlign: "left", background: "transparent", border: "none", cursor: "pointer", padding: 0, color: INK }}>
                      <div style={{ color: INK, fontFamily: FONTS.body, fontSize: 14, marginBottom: 4 }}>{s.title}</div>
                      <div style={{ color: MUTED_36, fontFamily: FONTS.body, fontSize: 11, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {(s.main_draft || s.raw_text || "").slice(0, 140)}
                      </div>
                      <div style={{ color: MUTED_36, fontFamily: FONTS.body, fontSize: 10, marginTop: 4, letterSpacing: "0.14em" }}>
                        {s.updated_at ? new Date(s.updated_at).toLocaleString() : ""}
                      </div>
                    </button>
                    <button onClick={() => removeScript(s.script_id)}
                      data-testid={`delete-script-${s.script_id}`}
                      style={capsuleStyle({ active: false, size: "sm", rgb: "255,122,102" })}>
                      delete
                    </button>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ children }) {
  return (
    <div style={{ color: "var(--role-coral-label-75)", fontFamily: FONTS.body, fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", marginBottom: 12 }}>
      {children}
    </div>
  );
}

function DraftHeader({ label, hint, colored }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 10 }}>
      <span style={{ color: colored ? "var(--role-green-text)" : "var(--role-blue-text)", fontFamily: FONTS.body, fontSize: 12, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600 }}>{label}</span>
      {hint && <span style={{ color: MUTED_36, fontFamily: FONTS.body, fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase" }}>· {hint}</span>}
    </div>
  );
}

// ---- Phone (≤600px) body — v6 block 07 ------------------------------------
const PHONE_CHIP = {
  height: 30, borderRadius: 999, border: "none", padding: "0 12px", display: "inline-flex", alignItems: "center", gap: 7,
  fontFamily: FONTS.body, fontSize: 10.5, letterSpacing: "0.08em", whiteSpace: "nowrap", cursor: "pointer", flex: "none",
};

function PhoneChips({ characters, editing, onToggleEdit, onTap, onRename, onDelete, onAdd, flashId }) {
  return (
    <div data-testid="sw-phone-characters" style={{ marginBottom: 14 }}>
      <Section>{editing ? "Edit · × removes · double tap renames" : "Characters · tap to insert · double tap to rename"}</Section>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {characters.map((c) => {
          const color = colorForKey(c.colorKey) || COLORS.textMuted;
          const lit = flashId === c.id;
          return (
            <button key={c.id} type="button" data-testid={`chip-${c.id}`}
              onPointerDown={(e) => e.preventDefault()}
              onPointerUp={(e) => { e.preventDefault(); onTap(c); }}
              onClick={(e) => e.preventDefault()}
              style={{ ...PHONE_CHIP, touchAction: "manipulation", WebkitTapHighlightColor: "transparent", background: lit ? `${color}55` : `${color}18`,
                boxShadow: lit ? `inset 0 0 0 1px ${color}, 0 0 16px ${color}80` : `inset 0 0 0 1px ${color}`, color: COLORS.textPrimary, maxWidth: "100%",
                transition: "background-color .3s ease, box-shadow .3s ease" }}>
              <span style={{ width: 18, height: 18, borderRadius: 9, background: color, display: "grid", placeItems: "center", color: "var(--yuo-on-accent)", fontSize: 9, fontWeight: 700, flex: "none" }}>{c.order}</span>
              <span style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</span>
              {editing && (
                <span role="button" aria-label="Delete" data-testid={`chip-del-${c.id}`}
                  onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()}
                  onClick={(e) => { e.stopPropagation(); onDelete(c.id); }}
                  style={{ marginLeft: 2, color: COLORS.textMuted, fontSize: 15, lineHeight: 1 }}>×</span>
              )}
            </button>
          );
        })}
        <button type="button" data-testid="sw-phone-edit" onClick={onToggleEdit} aria-pressed={editing}
          style={{ ...PHONE_CHIP, letterSpacing: "0.2em", textTransform: "uppercase", fontSize: 9.5,
            background: editing ? "var(--role-coral-pill-bg-08)" : "var(--yuo-surface-5)",
            boxShadow: editing ? "var(--role-coral-pill-glow-22)" : "none", color: INK }}>
          Edit {editing ? "▴" : "▾"}
        </button>
      </div>
      {characters.length === 0 && !editing && (
        <div style={{ color: MUTED_36, fontFamily: FONTS.body, fontSize: 11, marginTop: 8 }}>No characters yet — Edit ▾ opens the palette.</div>
      )}
      {editing && (
        <div data-testid="sw-phone-palette" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
          {COLOR_SLOTS.map((s) => {
            const rgb = hexToRgb(s.color);
            return (
              <button key={s.key} type="button" data-testid={`palette-${s.key}`} onClick={() => onAdd(s.key)}
                style={{ ...PHONE_CHIP, color: COLORS.textPrimary,
                  background: `radial-gradient(120% 160% at 30% 0%, rgba(${rgb},0.24), rgba(${rgb},0.06))`,
                  boxShadow: `0 0 14px rgba(${rgb},0.22)` }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", flex: "none",
                  background: `radial-gradient(38% 34% at 34% 26%, #fff, ${s.color} 30%, rgba(${rgb},0.18) 62%, #05060c 100%)`,
                  boxShadow: `0 0 8px rgba(${rgb},0.7)` }} />
                <span style={{ fontWeight: 600 }}>+ {s.label}</span>
                <span style={{ color: COLORS.textMuted, fontSize: 9.5 }}>{s.hint}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function useKeyboardInset() {
  const [inset, setInset] = useState(0);
  useEffect(() => {
    const vv = window.visualViewport; if (!vv) return;
    const f = () => setInset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    f(); vv.addEventListener("resize", f); vv.addEventListener("scroll", f);
    return () => { vv.removeEventListener("resize", f); vv.removeEventListener("scroll", f); };
  }, []);
  return inset;
}

function PhoneCharSheet({ modal, draft, setDraft, onSave, onCancel, onDelete }) {
  const meta = slotMetaForKey(modal.colorKey);
  const color = colorForKey(modal.colorKey) || COLORS.textMuted;
  const kb = useKeyboardInset();
  const btn = { height: 44, borderRadius: 999, border: "none", padding: "0 18px", fontFamily: FONTS.body, fontSize: 10.5, letterSpacing: "0.2em", textTransform: "uppercase", cursor: "pointer", background: "var(--yuo-surface-5)", color: COLORS.textPrimary };
  return (
    <div data-testid="char-sheet-backdrop" onClick={onCancel}
      style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", flexDirection: "column", justifyContent: "flex-end", background: "var(--m-backdrop)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}>
      <div data-testid="char-modal" onClick={(e) => e.stopPropagation()}
        style={{ borderRadius: "32px 32px 0 0", background: "var(--m-sheet-bg)", padding: `10px 16px calc(24px + env(safe-area-inset-bottom, 0px) + ${kb}px)`, display: "flex", flexDirection: "column", gap: 12, transition: "padding .2s ease" }}>
        <div style={{ width: 40, height: 4, borderRadius: 999, background: "var(--m-grabber)", margin: "0 auto 4px" }} />
        <div style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase" }}>{modal.edit ? "Rename character" : "New character"}</div>
        <div style={{ color, fontFamily: FONTS.body, fontSize: 12, letterSpacing: "0.04em" }}>{meta.label} · {meta.hint}</div>
        <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onFocus={(e) => e.target.select()}
          onKeyDown={(e) => e.key === "Enter" && onSave()} data-testid="char-input"
          placeholder={meta.isLocation ? "INT. COFFEE SHOP — DAY" : "e.g. ALEX"}
          style={{ width: "100%", height: 48, boxSizing: "border-box", background: "var(--yuo-surface-5)", border: "none", outline: "none", borderRadius: 999, padding: "0 18px", color: COLORS.textPrimary, fontFamily: FONTS.body, fontSize: 15, textTransform: "uppercase", boxShadow: `inset 0 0 0 1px ${color}66` }} />
        <div style={{ display: "flex", gap: 8 }}>
          {modal.edit && <button type="button" data-testid="char-delete" onClick={onDelete} style={{ ...btn, color: COLORS.error }}>Delete</button>}
          <button type="button" data-testid="char-cancel" onClick={onCancel} style={btn}>Cancel</button>
          <button type="button" data-testid="char-save" onClick={onSave} style={{ ...btn, flex: 1, background: color, color: "var(--yuo-on-accent)", boxShadow: `0 0 16px ${color}4d` }}>{modal.edit ? "Save" : "Create"}</button>
        </div>
      </div>
    </div>
  );
}

function PhoneSegment({ activeDraft, setActiveDraft, onTranslate }) {
  const seg = (on, tone) => ({
    height: 34, borderRadius: 999, border: "none", cursor: "pointer", fontFamily: FONTS.body,
    fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", color: COLORS.textPrimary,
    background: on ? (tone === "blue" ? "var(--role-blue-pill-bg-08)" : "var(--role-green-pill-bg-06)") : "var(--yuo-surface-5)",
    boxShadow: on ? (tone === "blue" ? "var(--role-blue-pill-glow-26)" : "var(--role-green-pill-glow-22)") : "none",
  });
  return (
    <div data-testid="sw-phone-segment" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 12 }}>
      <button type="button" data-testid="active-raw" onClick={() => setActiveDraft("raw")} style={seg(activeDraft === "raw", "blue")}>Raw</button>
      <button type="button" data-testid="active-main" onClick={() => setActiveDraft("main")} style={seg(activeDraft === "main", "green")}>Main</button>
      <button type="button" data-testid="screenwriter-translate" onClick={onTranslate}
        style={{ ...seg(false, "green"), color: "var(--role-green-text)" }}>Translate</button>
    </div>
  );
}

function PhoneBody(p) {
  const isRaw = p.activeDraft === "raw";
  const tapRef = useRef({ id: null, t: 0, snap: null });
  const [flashId, setFlashId] = useState(null);
  const tapChip = (c) => {
    const tr = tapRef.current, now = Date.now();
    const ta = isRaw ? p.rawRef.current : p.mainRef.current;
    if (tr.id === c.id && now - tr.t < 280 && tr.snap) {
      const s = tr.snap;
      (s.draft === "raw" ? p.setRawText : p.setMainDraft)(s.text);
      setTimeout(() => { if (ta) { ta.focus({ preventScroll: true }); ta.setSelectionRange(s.start, s.end); } }, 0);
      tr.id = null; tr.snap = null; setFlashId(null);
      p.onRename(c);
      return;
    }
    const text = isRaw ? p.rawText : p.mainDraft;
    const focused = ta && document.activeElement === ta;
    const sel = focused ? { start: ta.selectionStart, end: ta.selectionEnd } : { start: text.length, end: text.length };
    (isRaw ? p.rawSelRef : p.mainSelRef).current = sel;
    tr.snap = { draft: p.activeDraft, text, ...sel }; tr.id = c.id; tr.t = now;
    p.onInsert(c);
    setTimeout(() => { if (ta) { ta.focus({ preventScroll: true }); const caret = sel.start + (ta.value.length - text.length); ta.setSelectionRange(caret, caret); } }, 0);
    setFlashId(c.id); setTimeout(() => setFlashId((v) => (v === c.id ? null : v)), 300);
  };
  const text = isRaw ? p.rawText : p.mainDraft;
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const tone = isRaw ? "blue" : "green";
  return (
    <div data-testid="sw-phone-body" style={{ padding: "6px 14px 40px", width: "100%" }}>
      <input value={p.title} onChange={(e) => p.setTitle(e.target.value)} placeholder="Untitled Script" data-testid="screenwriter-title"
        style={{ width: "100%", background: "var(--yuo-surface-5)", border: "none", outline: "none", borderRadius: 999, padding: "12px 18px",
          color: INK, fontFamily: FONTS.body, fontSize: 16, letterSpacing: -0.2, marginBottom: 16, boxShadow: "var(--role-coral-title-glow)" }} />

      <PhoneChips characters={p.characters} editing={p.editing} onToggleEdit={p.onToggleEdit}
        onTap={tapChip} onRename={p.onRename} onDelete={p.onDelete} onAdd={p.onAdd} flashId={flashId} />

      <PhoneSegment activeDraft={p.activeDraft} setActiveDraft={p.setActiveDraft} onTranslate={p.onTranslate} />

      <div data-testid={isRaw ? "sw-phone-raw-panel" : "sw-phone-main-panel"} style={{ padding: "14px 14px 12px", borderRadius: 24, background: `var(--role-${tone}-panel-bg)`, boxShadow: `var(--role-${tone}-panel-glow)`, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
          <span style={{ color: isRaw ? "var(--role-blue-text)" : "var(--role-green-text)", fontFamily: FONTS.body, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 600, whiteSpace: "nowrap" }}>
            {isRaw ? "Raw Draft" : "Main Draft"}
          </span>
          <div data-testid={isRaw ? "raw-char-count" : "main-char-count"} style={{ color: MUTED_36, fontFamily: FONTS.body, fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", whiteSpace: "nowrap" }}>
            {text.length} char · {words} words
          </div>
        </div>
        {isRaw ? (
          <textarea ref={p.rawRef} value={p.rawText} onChange={(e) => p.setRawText(e.target.value)}
            onSelect={(e) => (p.rawSelRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd })}
            data-testid="raw-draft" placeholder="Write freely — scenes, lines, fragments…"
            style={{ width: "100%", boxSizing: "border-box", background: "var(--yuo-paper)", border: "none", borderRadius: 18, padding: "14px 16px",
              color: COLORS.textPrimary, fontFamily: monoFontFamily, fontSize: 13.5, lineHeight: "21px", outline: "none", minHeight: 260, display: "block",
              boxShadow: "var(--role-blue-field-glow)" }} />
        ) : (
          <textarea ref={p.mainRef} value={p.mainDraft} onChange={(e) => p.setMainDraft(e.target.value)}
            onSelect={(e) => (p.mainSelRef.current = { start: e.target.selectionStart, end: e.target.selectionEnd })}
            data-testid="main-draft" placeholder="After you tap 'Send to Main Draft' the formatted screenplay appears here. Edit freely."
            style={{ width: "100%", boxSizing: "border-box", background: "var(--yuo-paper)", border: "none", borderRadius: 18, padding: "14px 16px",
              color: COLORS.textPrimary, fontFamily: monoFontFamily, fontSize: 13.5, lineHeight: "21px", outline: "none", minHeight: 300, display: "block",
              boxShadow: "var(--role-green-field-glow)" }} />
        )}
      </div>

      {p.error && <div style={{ color: COLORS.error, fontFamily: FONTS.body, fontSize: 12, marginBottom: 10 }}>{p.error}</div>}

      {isRaw ? (
        <button type="button" onClick={p.onFormat} disabled={p.formatting} data-testid="btn-send-main"
          style={{ width: "100%", height: 44, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, border: "none", borderRadius: 999,
            background: "var(--role-blue-pill-bg-strong)", boxShadow: "var(--role-blue-pill-glow-34)", color: "var(--yuo-ink)", fontFamily: FONTS.body,
            fontSize: 10.5, letterSpacing: "0.22em", textTransform: "uppercase", opacity: p.formatting ? 0.6 : 1, cursor: "pointer" }}>
          <span style={{ width: 13, height: 13, borderRadius: "50%", background: "radial-gradient(38% 34% at 34% 26%, #fff, #4F9DFF 22%, #05060c 100%)", boxShadow: "0 0 10px rgba(var(--role-blue-rgb),0.8)" }} />
          {p.formatting ? "…" : "Send to Main Draft"}
        </button>
      ) : (
        <button type="button" onClick={p.onExport} disabled={p.exporting} data-testid="btn-export"
          style={{ width: "100%", height: 44, border: "none", borderRadius: 999, background: "var(--role-green-pill-bg-06)", boxShadow: "var(--role-green-pill-glow-22)",
            color: COLORS.textPrimary, fontFamily: FONTS.body, fontSize: 10.5, letterSpacing: "0.22em", textTransform: "uppercase", cursor: "pointer", opacity: p.exporting ? 0.6 : 1 }}>
          {p.exporting ? "…" : "Export PDF"}
        </button>
      )}
    </div>
  );
}

const secondaryBtnStyle = {
  background: "transparent",
  border: `1px solid ${COLORS.border}`,
  color: COLORS.textPrimary,
  borderRadius: 999,
  padding: "10px 22px",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
  fontSize: 14,
  fontWeight: 500,
};

const activeChipStyle = (on) => ({
  padding: "5px 14px",
  borderRadius: 999,
  border: `1px solid ${on ? COLORS.mirror : COLORS.border}`,
  background: on ? `rgba(var(--role-blue-rgb),0.13)` : "transparent",
  color: on ? COLORS.mirror : COLORS.textSecondary,
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
  fontSize: 12,
  fontWeight: 600,
});

/* ============================================================
 * Translate Modal
 * ============================================================ */

const LANG_OPTIONS = [
  { code: "en", label: "English" },
  { code: "ru", label: "Russian" },
  { code: "pt", label: "Portuguese" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "uk", label: "Ukrainian" },
];

function TranslateModal({ scriptText, onClose, onApply }) {
  const [mode, setMode] = useState("full");
  const [selection, setSelection] = useState("");
  const [target, setTarget] = useState("en");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const translate = async () => {
    const text = mode === "full" ? scriptText : selection.trim();
    if (!text) return;
    setBusy(true);
    try {
      const res = await api.screenwriterTranslate({ text, target_language: target, is_full_script: mode === "full" });
      setPreview(res?.translated || "");
    } catch (e) { alert(e?.message || "Translation failed"); }
    finally { setBusy(false); }
  };

  const apply = () => { if (preview) { onApply(preview, mode === "full"); onClose(); } };

  return (
    <div className="yuo-modal-backdrop" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="translate-modal" style={{ margin: "auto", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: 20, width: "92%", maxWidth: 640, maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
        <div style={{ color: COLORS.mirror, fontFamily: FONTS.body, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>Living translation</div>
        <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 22, marginBottom: 14 }}>Translate script</div>

        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          {[{ v: "full", l: "Entire script" }, { v: "selection", l: "Selected portion" }].map((o) => (
            <button key={o.v} onClick={() => setMode(o.v)} data-testid={`translate-mode-${o.v}`}
              style={{ padding: "6px 14px", borderRadius: 999, border: `1px solid ${mode === o.v ? COLORS.mirror : COLORS.border}`, background: mode === o.v ? `rgba(var(--role-blue-rgb),0.13)` : "transparent", color: mode === o.v ? COLORS.mirror : COLORS.textSecondary, fontFamily: FONTS.body, fontSize: 12 }}
            >
              {o.l}
            </button>
          ))}
        </div>

        {mode === "selection" && (
          <textarea
            value={selection}
            onChange={(e) => setSelection(e.target.value)}
            placeholder="Paste the portion here…"
            rows={5}
            data-testid="translate-selection-input"
            style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12, color: COLORS.textPrimary, fontFamily: monoFontFamily, fontSize: 13, outline: "none", marginBottom: 12 }}
          />
        )}

        <div style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>Target language</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
          {LANG_OPTIONS.map((l) => (
            <button key={l.code} onClick={() => setTarget(l.code)} data-testid={`translate-lang-${l.code}`}
              style={{ padding: "5px 12px", borderRadius: 999, border: `1px solid ${target === l.code ? COLORS.mirror : COLORS.border}`, background: target === l.code ? `rgba(var(--role-blue-rgb),0.13)` : "transparent", color: target === l.code ? COLORS.mirror : COLORS.textSecondary, fontFamily: FONTS.body, fontSize: 12 }}
            >
              {l.label}
            </button>
          ))}
        </div>

        {preview !== null && (
          <>
            <div style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>Preview</div>
            <div data-testid="translate-preview" style={{ flex: 1, overflow: "auto", background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 12, color: COLORS.textPrimary, fontFamily: monoFontFamily, fontSize: 13, lineHeight: "20px", whiteSpace: "pre-wrap", marginBottom: 12, minHeight: 120 }}>
              {preview}
            </div>
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button onClick={onClose} style={{ padding: "10px 18px", color: COLORS.textSecondary, fontFamily: FONTS.body, fontSize: 13 }}>Cancel</button>
          {preview === null ? (
            <button onClick={translate} disabled={busy || (mode === "selection" && !selection.trim())} data-testid="translate-run"
              style={{ background: COLORS.mirror, color: "var(--yuo-on-accent)", padding: "10px 22px", borderRadius: 999, fontFamily: FONTS.body, fontSize: 13, fontWeight: 700, opacity: busy || (mode === "selection" && !selection.trim()) ? 0.5 : 1 }}
            >
              {busy ? "Translating…" : "Translate"}
            </button>
          ) : (
            <button onClick={apply} data-testid="translate-apply" style={{ background: COLORS.symbiosis, color: "var(--yuo-on-accent)", padding: "10px 22px", borderRadius: 999, fontFamily: FONTS.body, fontSize: 13, fontWeight: 700 }}>
              {mode === "full" ? "Replace Main Draft" : "Append to Main Draft"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * Live Import Modal — Where Source Messages (character-assigned)
 * ============================================================ */

function ImportModal({ onClose, activeDraft, characters, isMaster, onIntegrate }) {
  const [step, setStep] = useState("where");           // where | source | messages
  const [dest, setDest] = useState(activeDraft || "raw");
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [pickedSource, setPickedSource] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [activeCharId, setActiveCharId] = useState(null);
  const [assign, setAssign] = useState({});            // msgId -> charId
  const [error, setError] = useState(null);

  useEffect(() => {
    if (step !== "source") return;
    let cancelled = false;
    (async () => {
      setLoadingSources(true);
      try {
        const r = await api.screenwriterSources();
        if (!cancelled) setSources(r?.sources || r || []);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoadingSources(false); }
    })();
    return () => { cancelled = true; };
  }, [step]);

  const openSource = async (s) => {
    setPickedSource(s);
    setStep("messages");
    setLoadingMessages(true);
    setError(null);
    try {
      const r = await api.screenwriterSourceMessages(s.kind, s.id);
      setMessages(r?.messages || r || []);
    } catch (e) {
      setError(e?.message || "Could not load messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  const toggleMessage = (m) => {
    // YUO/assistant messages are Master-plan only
    if (m.role === "assistant" && !isMaster) return;
    setAssign((prev) => {
      const next = { ...prev };
      if (next[m.message_id]) {
        delete next[m.message_id];
      } else {
        if (!activeCharId) { setError("Pick a character first."); return prev; }
        next[m.message_id] = activeCharId;
        setError(null);
      }
      return next;
    });
  };

  const removeMessage = (m) => {
    setMessages((prev) => prev.filter((x) => x.message_id !== m.message_id));
    setAssign((prev) => { const n = { ...prev }; delete n[m.message_id]; return n; });
  };

  const charById = (id) => characters.find((c) => c.id === id);

  const integrate = () => {
    const selected = messages.filter((m) => assign[m.message_id]);
    if (selected.length === 0) return;
    const chunks = selected.map((m) => {
      const c = charById(assign[m.message_id]);
      const name = (c?.name || "").toUpperCase();
      return name ? `${name}: ${m.content}` : m.content;
    });
    onIntegrate({ text: chunks.join("\n\n"), destination: dest });
  };

  const selectedCount = Object.keys(assign).length;
  const anyAssistant = messages.some((m) => m.role === "assistant");

  return (
    <div className="yuo-modal-backdrop" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="import-modal" style={{ margin: "auto", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: 20, width: "92%", maxWidth: 720, maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <div>
            <div style={{ color: COLORS.mirror, fontFamily: FONTS.body, fontSize: 11, letterSpacing: 2, textTransform: "uppercase" }}>Live Import</div>
            <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 22 }}>
              {step === "where" ? "Where to add — and from where?" : step === "source" ? "Pick a source" : `From ${pickedSource?.title || pickedSource?.kind || "source"}`}
            </div>
          </div>
          <button onClick={onClose} data-testid="import-close" style={{ color: COLORS.textSecondary, fontSize: 26 }}>×</button>
        </div>

        {step === "where" && (
          <>
            <div style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", margin: "10px 0 6px" }}>Where to add</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              {[{ v: "raw", l: "Raw Draft", h: "free-form scratch pad" }, { v: "main", l: "Main Draft", h: "straight into the screenplay" }].map((o) => (
                <button key={o.v} onClick={() => setDest(o.v)} data-testid={`import-dest-${o.v}`}
                  style={{ flex: 1, minWidth: 220, textAlign: "left", padding: 14, borderRadius: 12, border: `1px solid ${dest === o.v ? COLORS.mirror : COLORS.border}`, background: dest === o.v ? `rgba(var(--role-blue-rgb),0.13)` : "transparent" }}
                >
                  <div style={{ color: dest === o.v ? COLORS.mirror : COLORS.textPrimary, fontFamily: FONTS.heading, fontSize: 16 }}>{o.l}</div>
                  <div style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 12, marginTop: 2 }}>{o.h}</div>
                </button>
              ))}
            </div>
            <button onClick={() => setStep("source")} data-testid="import-next-source"
              style={{ background: `rgba(var(--role-blue-rgb),0.13)`, border: `1px solid rgba(var(--role-blue-rgb),0.4)`, color: COLORS.mirror, borderRadius: 12, padding: "12px 16px", fontFamily: FONTS.body, fontSize: 14, fontWeight: 700, marginTop: 4 }}
            >
              Pick a source
            </button>
          </>
        )}

        {step === "source" && (
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loadingSources ? (
              <div style={{ padding: 30, textAlign: "center", color: COLORS.textMuted }}>Loading…</div>
            ) : sources.length === 0 ? (
              <div style={{ padding: 30, textAlign: "center", color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 13 }}>Nothing to import yet.</div>
            ) : (
              sources.map((s) => {
                const type = (s.kind || "").toLowerCase();
                const badge = type === "diary" ? "Diary" : type === "content" ? "Creator" : type === "conversation" ? "Mirror" : (s.kind || "Mirror");
                return (
                  <button key={`${s.kind}-${s.id}`} onClick={() => openSource(s)} data-testid={`import-source-${s.id}`}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: 12, borderRadius: 12, border: `1px solid ${COLORS.border}`, background: COLORS.bg, marginBottom: 8 }}
                  >
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <span style={{ padding: "3px 10px", borderRadius: 6, background: COLORS.surface, color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>{badge}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: COLORS.textPrimary, fontFamily: FONTS.body, fontSize: 15 }}>{s.title || "Untitled"}</div>
                        <div style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 12, marginTop: 4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {s.preview || "…"}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        )}

        {step === "messages" && (
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
            {anyAssistant && !isMaster && (
              <div data-testid="master-banner" style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "8px 12px", fontFamily: FONTS.body, fontSize: 12, color: COLORS.textSecondary, marginBottom: 10 }}>
                YUO reply import — Master plan only.
              </div>
            )}
            <div style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 11, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>
              Pick a character — the messages will be assigned to it
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {characters.length === 0 && (
                <div style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 12 }}>
                  Create characters first (Color palette above).
                </div>
              )}
              {characters.map((c) => {
                const color = colorForKey(c.colorKey) || COLORS.textMuted;
                const on = activeCharId === c.id;
                return (
                  <button key={c.id} onClick={() => { setActiveCharId(c.id); setError(null); }} data-testid={`import-char-${c.id}`}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "5px 10px", borderRadius: 999,
                      border: `1px solid ${color}`, background: on ? color : `${color}18`,
                    }}
                  >
                    <span style={{ width: 18, height: 18, borderRadius: 9, background: on ? "var(--yuo-on-accent)" : color, color: on ? color : "var(--yuo-on-accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800 }}>{c.order}</span>
                    <span style={{ color: on ? "var(--yuo-on-accent)" : COLORS.textPrimary, fontFamily: FONTS.body, fontSize: 12, fontWeight: 700 }}>{c.name}</span>
                  </button>
                );
              })}
            </div>

            {error && <div style={{ color: COLORS.error, fontFamily: FONTS.body, fontSize: 12, marginBottom: 8 }}>{error}</div>}

            <div style={{ overflowY: "auto", flex: 1, paddingRight: 4 }}>
              {loadingMessages ? (
                <div style={{ padding: 30, textAlign: "center", color: COLORS.textMuted }}>Loading…</div>
              ) : messages.length === 0 ? (
                <div style={{ padding: 30, textAlign: "center", color: COLORS.textMuted }}>No messages in this source.</div>
              ) : (
                messages.map((m) => {
                  const assignedChar = assign[m.message_id] ? charById(assign[m.message_id]) : null;
                  const assignedColor = assignedChar ? colorForKey(assignedChar.colorKey) : null;
                  const roleLabel = m.role === "assistant" ? "YUO" : "ME";
                  const locked = m.role === "assistant" && !isMaster;
                  return (
                    <div key={m.message_id} data-testid={`import-msg-${m.message_id}`}
                      onClick={() => !locked && toggleMessage(m)}
                      style={{
                        display: "flex", gap: 10, padding: 12, borderRadius: 12,
                        border: `1px solid ${assignedColor || COLORS.border}`,
                        background: assignedColor ? `${assignedColor}18` : COLORS.bg,
                        marginBottom: 8, cursor: locked ? "not-allowed" : "pointer",
                        opacity: locked ? 0.4 : 1, position: "relative",
                      }}
                    >
                      <span style={{ padding: "2px 8px", borderRadius: 6, background: COLORS.surface, color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 10, letterSpacing: 1, textTransform: "uppercase", height: 20 }}>{roleLabel}</span>
                      <div style={{ flex: 1, color: COLORS.textPrimary, fontFamily: FONTS.body, fontSize: 13, lineHeight: "20px", whiteSpace: "pre-wrap", overflow: "hidden" }}>
                        {(m.content || "").slice(0, 400)}
                        {(m.content || "").length > 400 ? " …" : ""}
                      </div>
                      {assignedChar && (
                        <span style={{ alignSelf: "flex-start", padding: "3px 10px", borderRadius: 999, border: `1px solid ${assignedColor}`, color: assignedColor, fontFamily: FONTS.body, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
                          {assignedChar.name}
                        </span>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); removeMessage(m); }} data-testid={`import-msg-remove-${m.message_id}`}
                        style={{ color: COLORS.textMuted, fontSize: 20, alignSelf: "flex-start", padding: "0 4px" }}
                        aria-label="Remove from list"
                      >
                        −
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, paddingTop: 12, borderTop: `1px solid ${COLORS.border}`, marginTop: 8 }}>
              <button onClick={() => { setStep("source"); setAssign({}); setActiveCharId(null); }} data-testid="import-back"
                style={{ padding: "8px 12px", color: COLORS.textSecondary, fontFamily: FONTS.body, fontSize: 13 }}
              >
                Back
              </button>
              <div style={{ color: COLORS.textMuted, fontFamily: FONTS.body, fontSize: 12 }}>
                {dest === "raw" ? "Raw Draft" : "Main Draft"}
              </div>
              <button onClick={integrate} disabled={selectedCount === 0} data-testid="import-integrate"
                style={{
                  background: selectedCount ? COLORS.library : COLORS.border,
                  color: selectedCount ? "var(--yuo-on-accent)" : COLORS.textMuted,
                  borderRadius: 999, padding: "10px 20px",
                  fontFamily: FONTS.body, fontSize: 13, fontWeight: 700,
                }}
              >
                Integrate ({selectedCount})
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
